import { useCallback, useEffect, useState } from 'react';
import { chaptersApi } from '../lib/api';

// Loads chapters for a codex and exposes create + updateContent actions.
export default function useChapters(codexId) {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchChapters = useCallback(
    async (signal) => {
      setLoading(true);
      setError(null);
      try {
        const data = await chaptersApi.listByCodex(codexId, signal);
        setChapters(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err.name !== 'AbortError') setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [codexId]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchChapters(controller.signal);
    return () => controller.abort();
  }, [fetchChapters]);

  // Patch a chapter's fields in local state — no refetch, no flicker. Used by
  // both the optimistic local update and incoming realtime events.
  const applyChapterPatch = useCallback((chapterId, fields) => {
    setChapters((prev) =>
      prev.map((c) => (c.id === chapterId ? { ...c, ...fields } : c))
    );
  }, []);

  const createChapter = useCallback(
    async (title, { parentId = null, description = '' } = {}) => {
      const { id } = await chaptersApi.create({ codexId, title, parentId, description });
      await fetchChapters();
      return id;
    },
    [codexId, fetchChapters]
  );

  const updateChapterContent = useCallback(
    async (chapterId, content) => {
      await chaptersApi.updateContent(chapterId, content);
      await fetchChapters();
    },
    [fetchChapters]
  );

  // Optimistic patch: update local immediately, persist any of {title,description,hidden}.
  // Server broadcasts a structural or lightweight event depending on the field.
  const patchChapter = useCallback(
    async (chapterId, fields) => {
      const allowed = {};
      if (fields.title !== undefined) allowed.title = fields.title;
      if (fields.description !== undefined) allowed.description = fields.description;
      if (fields.hidden !== undefined) allowed.hidden = fields.hidden;
      applyChapterPatch(chapterId, allowed);
      try {
        await chaptersApi.patch(chapterId, allowed);
      } catch (err) {
        await fetchChapters();
        throw err;
      }
    },
    [applyChapterPatch, fetchChapters]
  );

  const deleteChapter = useCallback(
    async (chapterId) => {
      const prev = chapters;
      setChapters((cur) => cur.filter((c) => c.id !== chapterId));
      try {
        await chaptersApi.delete(chapterId);
      } catch (err) {
        setChapters(prev);
        throw err;
      }
    },
    [chapters]
  );

  // Optimistic reorder: orderedIds is the full ordered list of IDs in the codex.
  const reorderChapters = useCallback(
    async (orderedIds) => {
      const prev = chapters;
      const byId = new Map(prev.map((c) => [c.id, c]));
      const next = orderedIds
        .map((id, i) => {
          const ch = byId.get(id);
          return ch ? { ...ch, position: i } : null;
        })
        .filter(Boolean);
      setChapters(next);
      try {
        await chaptersApi.reorder(codexId, orderedIds);
      } catch (err) {
        setChapters(prev);
        throw err;
      }
    },
    [chapters, codexId]
  );

  return {
    chapters,
    loading,
    error,
    refetch: fetchChapters,
    createChapter,
    updateChapterContent,
    patchChapter,
    deleteChapter,
    reorderChapters,
    applyChapterPatch,
  };
}
